import { ConfigType as DayjsInput } from "dayjs";

export type ContentType = "STANDALONE" | "EPISODE";

export const ContentTypeLabels: Record<ContentType, string> = {
    STANDALONE: "Standalone",
    EPISODE: "Episodic",
};

export type SubtitleFileFormat = "TTML" | "WEB_VTT" | "SRT";

export type ImageEntityInstance =
    | "CONTENT_PROVIDER_LOGO"
    | "POSTER_PORTRAIT_THUMBNAIL_SMALL"
    | "POSTER_PORTRAIT_THUMBNAIL_MEDIUM"
    | "POSTER_PORTRAIT_THUMBNAIL_LARGE"
    | "CLEAR_POSTER_PORTRAIT_THUMBNAIL_SMALL"
    | "CLEAR_POSTER_PORTRAIT_THUMBNAIL_MEDIUM"
    | "CLEAR_POSTER_PORTRAIT_THUMBNAIL_LARGE"
    | "POSTER_LANDSCAPE_STANDARD_HERO_SMALL"
    | "POSTER_LANDSCAPE_STANDARD_HERO_MEDIUM"
    | "POSTER_LANDSCAPE_WIDESCREEN_HERO_MEDIUM"
    | "POSTER_LANDSCAPE_WIDESCREEN_HERO_LARGE"
    | "POSTER_LANDSCAPE_WIDESCREEN_THUMBNAIL_MEDIUM"
    | "CLEAR_POSTER_LANDSCAPE_STANDARD_HERO_SMALL"
    | "CLEAR_POSTER_LANDSCAPE_STANDARD_HERO_MEDIUM"
    | "CLEAR_POSTER_LANDSCAPE_WIDESCREEN_HERO_MEDIUM"
    | "CLEAR_POSTER_LANDSCAPE_WIDESCREEN_HERO_LARGE"
    | "CLEAR_POSTER_LANDSCAPE_WIDESCREEN_THUMBNAIL_MEDIUM"
    | "STILL_FRAME_LANDSCAPE_WIDESCREEN_HERO_MEDIUM"
    | "STILL_FRAME_LANDSCAPE_WIDESCREEN_HERO_LARGE";

export type ImageType =
    | "UNKNOWN"
    | "POSTER"
    | "CLEAR_POSTER"
    | "BACKDROP"
    | "BACKDROP_CAST"
    | "STILL"
    | "PERSON"
    | "CHARACTER"
    | "LOGO"
    | "LOGO_CONTENT_PROVIDER";

export type ImageOrientation =
    | "UNKNOWN"
    | "NOT_APPLICABLE"
    | "PORTRAIT_3_4"
    | "LANDSCAPE_4_3"
    | "LANDSCAPE_16_9"
    | "LANDSCAPE_21_9"
    | "SQUARE_1_1";

export const ImageOrientationShortLabels: Record<ImageOrientation, string> = {
    UNKNOWN: "Unk",
    NOT_APPLICABLE: "N/A",
    PORTRAIT_3_4: "3:4",
    LANDSCAPE_4_3: "4:3",
    LANDSCAPE_16_9: "16:9",
    LANDSCAPE_21_9: "21:9",
    SQUARE_1_1: "1:1",
};

export const ImageOrientationLabels: Record<ImageOrientation, string> = {
    UNKNOWN: "Unknown",
    NOT_APPLICABLE: "Not Applicable",
    PORTRAIT_3_4: "Standard (3:4)",
    LANDSCAPE_4_3: "Standard (4:3)",
    LANDSCAPE_16_9: "Widescreen (16:9)",
    LANDSCAPE_21_9: "CinemaScope (21:9)",
    SQUARE_1_1: "Square (1:1)",
};

export type ImagePurpose = "NOT_APPLICABLE" | "THUMBNAIL" | "HERO" | "MOOD";

export const ImagePurposeShortLabels: Record<ImagePurpose, string> = {
    NOT_APPLICABLE: "N/A",
    THUMBNAIL: "Thumb",
    HERO: "Hero",
    MOOD: "Mood",
};

export const ImagePurposeLabels: Record<ImagePurpose, string> = {
    NOT_APPLICABLE: "Not Applicable",
    THUMBNAIL: "Thumbnail",
    HERO: "Hero",
    MOOD: "Mood",
};

export type ImageSize = "NOT_APPLICABLE" | "SMALL" | "MEDIUM" | "LARGE";

export const ImageSizeShortLabels: Record<ImageSize, string> = {
    NOT_APPLICABLE: "N/A",
    SMALL: "Sml",
    MEDIUM: "Med",
    LARGE: "Lrg",
};

export const ImageSizeLabels: Record<ImageSize, string> = {
    NOT_APPLICABLE: "Not Applicable",
    SMALL: "Small",
    MEDIUM: "Medium",
    LARGE: "Large",
};

export type OfferType = "AVOD" | "SVOD";

export type OfferLevel = "NOT_APPLICABLE" | "CONTENT" | "CONTENT_GROUP";

export type AudioType =
    | "UNKNOWN"
    | "MONO"
    | "STEREO"
    | "SURROUND_SOUND"
    | "DOLBY_DIGITAL_5_1";

export type VideoType = "UNKNOWN" | "SD" | "HD" | "THREE_D" | "UHD4K";

export type ContentGroupType = "BOX_SET" | "SERIES" | "SEASON";

export const ContentGroupTypeLabels: Record<ContentGroupType, string> = {
    BOX_SET: "Box Set",
    SERIES: "Series",
    SEASON: "Season",
};

export interface Bookmark {
    startOffsetMS?: number | null;
    endOffsetMS?: number | null;
}

export interface BreakPosition {
    breakOutOffsetMS?: number | null;
    breakInOffsetMS?: number | null;
}

export interface Bookmarks {
    intro1?: Bookmark | null;
    intro2?: Bookmark | null;
    recap1?: Bookmark | null;
    recap2?: Bookmark | null;
    closingCredits?: Bookmark | null;
}

export interface Catalogue {
    id?: string | null;
    presentable?: boolean | null;
    purchasable?: boolean | null;
    playable?: boolean | null;
}

export interface ContentGroupLink {
    contentGroupType?: ContentGroupType | null;
    contentGroupId?: string | null;
    contentGroupProviderAssetId?: string | null;
    order?: number | null;
}

export interface Summaries {
    longSynopsis?: string | null;
    mediumSynopsis?: string | null;
    shortSynopsis?: string | null;
}

export interface ParentalRating {
    region?: string | null;
    system?: string | null;
    label?: string | null;
    advisories?: string[] | null;
}

export interface ReleaseInfo {
    yearOfRelease?: number | null;
    countriesOfOrigin?: string[] | null;
    studio?: string | null;
}

export interface CastAndCrew {
    actors?: string[] | null;
    directors?: string[] | null;
    producers?: string[] | null;
}

export interface Classification {
    categories?: string[] | null;
    genres?: string[] | null;
    keywords?: string[] | null;
    audience?: string | null;
}

export interface Image {
    type?: ImageType | null;
    purpose?: ImagePurpose | null;
    url?: string | null;
    width?: number | null;
    height?: number | null;
    mimeType?: string | null;
    fileSize?: number | null;
    md5?: string | null;
    orientation?: ImageOrientation | null;
    size?: ImageSize | null;
    preprocessed?: boolean | null;
}

export interface PreviewVideoAsset {
    url?: string | null;
    runTime?: string | null;
    audioType?: AudioType | null;
    videoType?: VideoType | null;
    parentalRatings?: ParentalRating[] | null;
    order?: number | null;
}

export interface VideoAsset {
    url?: string | null;
    runTime?: string | null;
    languages?: string[] | null;
    audioType?: AudioType | null;
    videoType?: VideoType | null;
    breakPositions?: BreakPosition[] | null;
    bookmarks?: Bookmarks | null;
    order?: number | null;
}

export interface SubtitleAsset {
    url?: string | null;
    fileFormat?: SubtitleFileFormat | null;
    language?: string | null;
    order?: number | null;
}

export interface LicensingWindow {
    startDateTime?: DayjsInput | null;
    endDateTime?: DayjsInput | null;
}

export interface Monetary {
    currencyCode?: string | null;
    amount?: number | null;
}

export interface OfferWindow {
    offerType?: OfferType | null;
    offerLevel?: OfferLevel | null;
    startDateTime?: DayjsInput | null;
    endDateTime?: DayjsInput | null;
    catalogues?: Catalogue[] | null;
    tenantId?: string | null;
    name?: string | null;
    description?: string | null;
    productName?: string | null;
    depCode?: string | null;
    suggestedPrice?: Monetary | null;
    displayDescription?: string | null;
}

export interface ContentGroup {
    id?: string | null;
    sequence?: number | null;
    providerAssetId?: string | null;
    providerId?: string | null;
    providerName?: string | null;
    contentPartnerId?: string | null;
    contentPartnerName?: string | null;
    product?: string | null;

    contentGroupLinks?: ContentGroupLink[] | null;

    contentGroupType?: ContentGroupType | null;
    title?: string | null;
    summaries?: Summaries | null;
    parentalRatings?: ParentalRating[] | null;
    releaseInfo?: ReleaseInfo | null;
    castAndCrew?: CastAndCrew | null;
    classification?: Classification | null;

    sequenceNumber?: number | null;

    images?: Partial<Record<ImageEntityInstance, Image>> | null;

    previewVideoAssets?: PreviewVideoAsset[] | null;

    licensingWindow?: LicensingWindow | null;

    createdDateTime?: DayjsInput | null;
    lastModifiedDateTime?: DayjsInput | null;
    version?: number | null;
}

export interface Content {
    id?: string | null;
    sequence?: number | null;
    providerAssetId?: string | null;
    providerId?: string | null;
    providerName?: string | null;
    contentPartnerId?: string | null;
    contentPartnerName?: string | null;
    product?: string | null;

    contentGroupLinks?: ContentGroupLink[] | null;

    contentType?: ContentType | null;
    title?: string | null;
    summaries?: Summaries | null;
    parentalRatings?: ParentalRating[] | null;
    releaseInfo?: ReleaseInfo | null;
    castAndCrew?: CastAndCrew | null;
    classification?: Classification | null;

    contentGroupSequenceNumber?: number | null;
    totalContentGroupItems?: number | null;
    sequenceTitle?: string | null;
    sequenceNumber?: number | null;
    premier?: boolean | null;
    finale?: boolean | null;

    images?: Partial<Record<ImageEntityInstance, Image>> | null;

    previewVideoAssets?: PreviewVideoAsset[] | null;
    videoAsset?: VideoAsset | null;
    subtitleAssets?: SubtitleAsset[] | null;

    licensingWindow?: LicensingWindow | null;
    offerWindows?: OfferWindow[] | null;

    createdDateTime?: DayjsInput | null;
    lastModifiedDateTime?: DayjsInput | null;
    version?: number | null;
}

export type FpbSummaryAssetType = "STANDALONE"
    | "EPISODE"
    | "BOX_SET"
    | "SERIES"
    | "SEASON"

export type ClassificationMethod = "FPB"
    | "SELF_CLASSIFIED"
    | "DISTRIBUTOR"

export const ClassificationMethodLabels: Record<ClassificationMethod, string> = {
    FPB: "FPB",
    SELF_CLASSIFIED: "Self Classified",
    DISTRIBUTOR: "Distributor",
};

export interface FpbSummary {
    assetId?: string | null;
    contentPartnerId?: string | null;
    contentPartnerName?: string | null;

    title?: string | null;
    seasonNumberAndEpisodeNumber?: string | null;
    categories?: string[] | null;
    runTime?: string | null;
    ageRating?: string | null;
    advisories?: string[] | null;

    certificateReferenceNumber?: string | null;
    classificationMethod?: ClassificationMethod | null;
    classificationIssueDate?: DayjsInput | null;
    certificateLocation?: string | FileList | null;

    itemId?: string | null;
    itemType?: FpbSummaryAssetType | null;
    licensingWindow?: LicensingWindow | null;

    createdDateTime?: DayjsInput | null;
    lastModifiedDateTime?: DayjsInput | null;
    version?: number | null;
}

export type ImageEntityInstanceType =
    | "POSTER_PORTRAIT"
    | "CLEAR_POSTER_PORTRAIT"
    | "POSTER_LANDSCAPE"
    | "CLEAR_POSTER_LANDSCAPE"
    | "STILL_FRAME_LANDSCAPE"
    | "CONTENT_PROVIDER_LOGO"

export interface ImageUpload {
    asset: Content | ContentGroup;
    file: File;
    imageType: string;
    assetType: string;
}

export type OfferWindowWithTenantName = OfferWindow & {
    tenantName?: string
}

export type OfferWindowRow = OfferWindowWithTenantName & {
    isNew?: boolean;
    ingestorId?: string;
}

export type OfferWindowFormValues = {
    offerWindows: OfferWindowRow[]
}